import { useState, useCallback } from 'react';
import { apiService } from '../services/api.ts';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { isAuthSessionExpiredError } from '../services/authService.tsx';

export const useCustomers = (token) => {
  const { t } = useTranslation();
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedCustomerName, setSelectedCustomerName] = useState('');
  const [isCustomerSelected, setIsCustomerSelected] = useState(false);

  const fetchCustomers = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await apiService.getCustomers(token);
      setCustomers(response);
    } catch (error) {
      if (isAuthSessionExpiredError(error)) return;
      toast.error(t('error_fetching_customers'), { position: "bottom-left" });
    }
  }, [token, t]);

  const addCustomer = useCallback(async (customerData) => {
    if (!token) return;
    
    try {
      const response = await apiService.createCustomer(token, customerData);
      setCustomers(prev => [...prev, response]);
      toast.success(t('customer_added'), { position: "bottom-left" });
      return response;
    } catch (error) {
      if (isAuthSessionExpiredError(error)) return;
      toast.error(t('error_adding_customer'), { position: "bottom-left" });
    }
  }, [token, t]);

  const deleteCustomer = useCallback(async (customerId) => {
    if (!token) return;
    
    try {
      await apiService.deleteCustomer(token, customerId);
      setCustomers(prev => prev.filter(customer => customer.customer_id !== customerId));
      if (selectedCustomer?.customer_id === customerId) {
        setSelectedCustomer(null);
        setSelectedCustomerName('');
        setIsCustomerSelected(false);
      }
      toast.success(t('customer_deleted'), { position: "bottom-left" });
    } catch (error) {
      if (isAuthSessionExpiredError(error)) return;
      toast.error(t('error_deleting_customer'), { position: "bottom-left" });
    }
  }, [token, selectedCustomer, t]);

  const selectCustomer = useCallback((customer) => {
    setSelectedCustomer(customer);
    setSelectedCustomerName(customer.name);
    setIsCustomerSelected(true);
  }, []);

  const clearSelectedCustomer = useCallback(() => {
    setSelectedCustomer(null);
    setSelectedCustomerName('');
    setIsCustomerSelected(false);
  }, []);

  return {
    customers,
    selectedCustomer,
    selectedCustomerName,
    isCustomerSelected,
    fetchCustomers,
    addCustomer,
    deleteCustomer,
    selectCustomer,
    clearSelectedCustomer,
    setSelectedCustomerName,
    setIsCustomerSelected
  };
};
